/* eslint-disable no-nested-ternary */
/* eslint-disable no-param-reassign */
import {
  useState, useEffect, useCallback,
} from 'react';
import { getInvestorAssets } from 'modules/investmentAssets/repository';
import { useSelector } from 'react-redux';

export function useInvestorAssets(organizations, groupBy, refreshFlag) {
  const [loading, setLoading] = useState(true);
  const [operations, setOperations] = useState(null);
  const [originalOperations, setOriginalOperations] = useState(null);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  const { currency, currencies } = useSelector(state => state.global.investments);

  const calculateStats = useCallback((ops) => {
    if (!ops?.length) {
      return {
        totalValuation: 0,
        realizedValue: 0,
        totalInvested: 0,
        lastDate: null,
        multiple: 0,
      };
    }

    const auxStats = ops.reduce((acc, curr) => ({
      totalValuation: acc.totalValuation + (curr?.value ?? 0),
      realizedValue: acc.realizedValue + (curr?.realized ?? 0),
      totalInvested: acc.totalInvested + (curr?.invested ?? 0),
      lastDate: curr?.date && (!acc.lastDate || new Date(curr.date) > new Date(acc.lastDate))
        ? new Date(curr.date) : acc.lastDate,
    }), {
      totalValuation: 0,
      realizedValue: 0,
      totalInvested: 0,
      lastDate: null,
    });

    return {
      ...auxStats,
      multiple: auxStats.totalInvested > 0 ? auxStats.totalValuation / auxStats.totalInvested : 0,
    };
  }, []);

  const convertCurrency = useCallback((value, assetCurrency) => {
    if (!value) return 0;
    const rate = currencies[assetCurrency || 'EUR'] || 1;
    return value / rate;
  }, [currencies]);

  const processExchangeRates = useCallback((operation) => {
    const processDetails = detail => ({
      ...detail,
      value: convertCurrency(detail.value, detail.asset?.currency),
      invested: convertCurrency(detail.invested, detail.asset?.currency),
      realized: convertCurrency(detail.realized, detail.asset?.currency),
    });

    return {
      ...operation,
      value: convertCurrency(operation.value, operation.asset?.currency),
      invested: convertCurrency(operation.invested, operation.asset?.currency),
      realized: convertCurrency(operation.realized, operation.asset?.currency),
      rowDetails: operation.rowDetails?.map(processDetails),
    };
  }, [convertCurrency]);

  useEffect(() => {
    if (!originalOperations || currencies?.[currency] !== 1) return;

    try {
      const exchangedOperations = originalOperations.items.map(processExchangeRates);

      setOperations(prev => ({
        ...prev,
        total: originalOperations.total,
        items: exchangedOperations,
        investments: originalOperations.investments,
        count: (prev?.count || 0) + 1,
      }));

      setStats(calculateStats(exchangedOperations));
    } catch (err) {
      setError('Error processing currency conversion');
      console.error('Currency conversion error:', err);
    }
  }, [currency, currencies, originalOperations, processExchangeRates, calculateStats]);

  const groupInvestments = useCallback((items, groupKeys) => {
    const createEmptyGroup = (key, item) => ({
      id: key,
      items: [],
      value: 0,
      invested: 0,
      realized: 0,
      name: item[groupKeys[0]],
      rowDetails: [],
      keys: groupKeys.reduce((obj, k) => ({ ...obj, [k]: item[k] }), {}),
      status: null,
      type: item.type || 'other',
      date: null,
    });

    const createDetail = (item) => item.rowDetails || (item.organization ? [{
      id: item.id,
      name: item.name,
      value: item.value || 0,
      invested: item.invested || 0,
      realized: item.realized || 0,
      asset: item.asset,
      organization: item.organization,
      status: item.status,
      type: item.type,
      date: item.date,
    }] : []);

    const grouped = items.reduce((groups, item) => {
      const key = groupKeys.map(k => item[k] || 'undefined').join('-');

      if (!groups[key]) {
        groups[key] = createEmptyGroup(key, item);
      }

      groups[key].items.push(item);
      groups[key].value += item.value || 0;
      groups[key].invested += item.invested || 0;
      groups[key].realized += item.realized || 0;

      if (item.date && (!groups[key].date || new Date(item.date) > new Date(groups[key].date))) {
        groups[key].date = item.date;
      }

      groups[key].rowDetails.push(...createDetail(item));

      return groups;
    }, {});

    return Object.values(grouped).map(group => {
      const statuses = new Set(group.rowDetails.map(d => d.status).filter(Boolean));
      const types = new Set(group.rowDetails.map(d => d.type).filter(Boolean));

      const status = statuses.size > 1
        ? (groupKeys.includes('status') ? Array.from(statuses)[0] : 'partial')
        : statuses.size === 1 ? Array.from(statuses)[0] : null;

      const type = types.size > 1 ? 'mixed' : types.size === 1 ? Array.from(types)[0] : null;

      return { ...group, status, type };
    });
  }, []);

  const processOrganizationTotals = useCallback((orgs) => orgs.reduce((sum, org) => {
    const op = org.lastOperation || {};
    const newDate = op.date && (!sum.date || new Date(op.date) > new Date(sum.date))
      ? op.date
      : sum.date;

    const newStatus = !sum.status
      ? op.status
      : sum.status === op.status
        ? op.status
        : groupBy === 'status' ? sum.status : 'partial';

    return {
      value: sum.value + (op.value || 0),
      invested: sum.invested + (op.invested || 0),
      realized: sum.realized + (op.realized || 0),
      date: newDate,
      status: newStatus,
    };
  }, {
    value: 0, invested: 0, realized: 0, date: null, status: undefined,
  }), [groupBy]);

  const createRowDetail = (id, name, org, type, lastOperation, asset) => ({
    id,
    name,
    value: lastOperation?.value || 0,
    invested: lastOperation?.invested || 0,
    realized: lastOperation?.realized || 0,
    asset,
    organization: { id: org.id, name: org.name, permissions: org.permissions },
    type,
    status: lastOperation?.status,
    date: lastOperation?.date,
  });

  const mapOperation = useCallback(({ organizations: orgs, asset, ...rest }) => {
    const filteredOrgs = organizations ? orgs?.filter(org => organizations.includes(org.id)) : orgs;

    if (groupBy === 'status') {
      const operationsByStatus = filteredOrgs.reduce((acc, org) => {
        const status = org.lastOperation?.status || 'undefined';
        if (!acc[status]) acc[status] = [];
        acc[status].push(org);
        return acc;
      }, {});

      return Object.entries(operationsByStatus).map(([status, statusOrgs]) => ({
        ...rest,
        ...processOrganizationTotals(statusOrgs),
        name: asset.name,
        logo: asset.logo,
        status,
        rowDetails: statusOrgs.map(org => createRowDetail(rest.id, asset.name, org, rest.type, org.lastOperation, asset)),
      }));
    }

    const baseOperation = {
      ...rest,
      ...processOrganizationTotals(filteredOrgs),
      name: asset.name,
      logo: asset.logo,
      asset,
    };

    if (filteredOrgs.length > 1) {
      return {
        ...baseOperation,
        rowDetails: filteredOrgs.map(org => createRowDetail(rest.id, asset.name, org, rest.type, org.lastOperation, asset)),
      };
    }

    if (filteredOrgs.length === 1) {
      return {
        ...baseOperation,
        organization: { id: filteredOrgs[0].id, name: filteredOrgs[0].name, permissions: filteredOrgs[0].permissions },
      };
    }

    return baseOperation;
  }, [groupBy, organizations, processOrganizationTotals]);

  useEffect(() => {
    const fetchOperations = async () => {
      try {
        setOriginalOperations(null);
        setLoading(true);

        const { items } = await getInvestorAssets({ organizations });

        const mappedOperations = items.map(mapOperation).flat();

        const finalOperations = !['type', 'status'].includes(groupBy)
          ? mappedOperations
          : groupInvestments(mappedOperations, [groupBy]);

        setOriginalOperations({
          items: finalOperations,
          total: finalOperations.length,
          investments: mappedOperations.length,
        });
      } catch (err) {
        setError('Error fetching operations');
        console.error('Fetch operations error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchOperations();
  }, [organizations, groupBy, refreshFlag, mapOperation, groupInvestments]);

  return {
    loading,
    operations,
    stats,
    error,
  };
}
